import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetExplodingFeeScheduleByIdQuery,
  useCreateExplodingFeeScheduleMutation,
} from "store/queries/explodingFeeSchedule";

import {
  ExplodingFeeScheduleForm,
  IExplodingFeeScheduleFormData,
  INITIAL_FORM_DATA,
} from "./components/ExplodingFeeScheduleForm";

export function CopyExplodingFeeSchedulePage() {
  useDocumentTitle("Copy Exploding Fee Schedule");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateExplodingFeeScheduleMutation();
  const { data: rulesetData, isLoading: rulesetLoading } =
    useGetExplodingFeeScheduleByIdQuery(id!);

  const [formData, setFormData] =
    useState<IExplodingFeeScheduleFormData>(INITIAL_FORM_DATA);

  useEffect(() => {
    if (rulesetData?.data) {
      const r = rulesetData.data;
      setFormData({
        providerId: asDefault ? null : r.providerId,
        payerId: asDefault ? null : r.payerId,
        cptCodes: r.cptCodes,
        totalRateInCents: r.totalRateInCents,
        groupName: r.groupName ? `${r.groupName} (Copy)` : "",
        isDefault: asDefault,
        contractLanguage: r.contractLanguage ?? "",
      });
    }
  }, [rulesetData, asDefault]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        cptCodes: formData.cptCodes,
        totalRateInCents: formData.totalRateInCents!,
        groupName: formData.groupName.trim() || undefined,
        contractLanguage: formData.contractLanguage.trim() || null,
      };
      if (formData.isDefault) {
        payload.isDefault = true;
      } else {
        payload.providerId = formData.providerId!;
        payload.payerId = formData.payerId!;
      }
      await createRuleset(payload).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: formData.isDefault
          ? "Exploding fee schedule default created."
          : "Exploding fee schedule ruleset copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message ||
          "Failed to copy exploding fee schedule ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (rulesetLoading) return <LoadingSpinner />;

  return (
    <ExplodingFeeScheduleForm
      title={
        asDefault
          ? "Create Default Exploding Fee Schedule Ruleset"
          : "Copy Exploding Fee Schedule Ruleset"
      }
      submitLabel={asDefault ? "Create Default" : "Create Copy"}
      submitIcon="pi pi-copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
